// Central configuration for the Fintepla Kommunicate embed.
//
// Precedence (highest wins):
//   1. Explicit overrides passed to setupKommunicateUi(overrides)
//   2. window.FinteplaChat (set by the host page before the embed loads)
//   3. data-* attributes on the <script> tag that loaded the embed
//   4. Vite build-time env (VITE_KOMMUNICATE_*, VITE_ANALYTICS_*)
//   5. Per-environment baked defaults (__KOMMUNICATE_APP_ID__ /
//      __KOMMUNICATE_BOT_ID__ — see vite.config.js) and the hard-coded
//      defaults below.
//
// The IIFE embed (src/embed.js) captures (2) and (3) at script execution time
// and passes them in as `overrides`. The dev playground (src/main.js) passes
// its own overrides directly.

// Per-environment Kommunicate IDs are injected by Vite's `define` at
// build/serve time. The fallbacks here only matter if this module is
// imported outside Vite (e.g. raw vitest run with no transform); in
// that case we assume staging values so tests stay deterministic.
const BAKED_APP_ID =
  typeof __KOMMUNICATE_APP_ID__ !== 'undefined'
    ? __KOMMUNICATE_APP_ID__
    : '21a7463d78c3d80eca935a6015dd7a198'
const BAKED_BOT_ID =
  typeof __KOMMUNICATE_BOT_ID__ !== 'undefined'
    ? __KOMMUNICATE_BOT_ID__
    : 'faye-tpf2k'

const DEFAULTS = {
  appId: import.meta.env.VITE_KOMMUNICATE_APP_ID || BAKED_APP_ID,
  botId: import.meta.env.VITE_KOMMUNICATE_BOT_ID || BAKED_BOT_ID,
  headerTitle: "Hi, I'm Faye",
  speakToPersonText: 'Speak to a Person',
  // Built-in Kommunicate greeting popup bubble. This mirrors the dashboard
  // feature ("chat popup message") so we can force-enable it without
  // re-enabling launcher message previews.
  chatPopupDelayMs: 12000,
  chatPopupMessageText: "Hi, I'm Faye! What can I help you with today?",
  // Max time the host pre-hides the iframe while the SDK loads (masks first-paint glitch).
  // After this, the launcher is shown even if `onInit` is late; theme still applies when ready.
  concealMaxMs: 1500,
  debug: false,

  // Frontend analytics transport. The backend is the system of record for
  // conversational events; the frontend only forwards UX / launch / CTA
  // events. All four fields are safe to omit — when `analyticsEndpoint` is
  // empty or `analyticsEnabled` is false the transport is a silent no-op.
  analyticsEndpoint: import.meta.env.VITE_ANALYTICS_ENDPOINT || '',
  analyticsEnabled: import.meta.env.VITE_ANALYTICS_ENABLED !== 'false',
  environment:
    import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE || 'production',
  debugAnalytics: import.meta.env.VITE_DEBUG_ANALYTICS === 'true',

  // Optional brand-token overrides consumed by src/widget-css.js → passed
  // through `customizeWidgetCss`. `undefined` means "use the value already
  // declared in theme.css". These let the host page re-theme the widget
  // without editing/rebuilding CSS.
  primaryColor: undefined,
  primaryColorHover: undefined,
  focusRingColor: undefined,
  headingFontFamily: undefined,
  bodyFontFamily: undefined,
}

export function resolveConfig(overrides = {}) {
  const cleaned = Object.fromEntries(
    Object.entries(overrides).filter(([, v]) => v !== undefined && v !== null),
  )
  return { ...DEFAULTS, ...cleaned }
}

export const KOMMUNICATE_SCRIPT_URL =
  'https://widget.kommunicate.io/v2/kommunicate.app'

// DOM selectors verified against the current Kommunicate build
// (widget.kommunicate.io/v2/kommunicate.app). If Kommunicate changes these in
// a future version, inspect the nodes in DevTools and update here.
export const SELECTORS = {
  launcherId: 'mck-sidebox-launcher',
  headerBox: '#mck-sidebox .mck-box-top',
  titleId: 'mck-tab-title',
  avatar: '#mck-sidebox .mck-box-top .mck-agent-image',
  footerContainerId: 'mck-box-form-container',
  // Parent that holds the header + message body + footer as siblings. The
  // resource strip is inserted into this container immediately after the
  // header, so it lives above the scrollable message area.
  contentContainerId: 'mck-sidebox-content',
  // Scrollable message list (`.mck-box-body`). Documented for DOM maps;
  // the 3px purple rule under the PDF strip is on `#fintepla-resource-strip`.
  messageBody: '#mck-sidebox-content > .mck-box-body',
}

export const SPEAK_TO_PERSON_BUTTON_ID = 'fintepla-speak-to-person-btn'

// Resource strip (two PDF links under the header).
export const RESOURCE_STRIP_ID = 'fintepla-resource-strip'

export const RESOURCE_LINKS = [
  {
    label: 'Medication Guide',
    url: 'https://www.ucb-usa.com/fintepla-medication-guide.pdf',
    side: 'left',
  },
  {
    label: 'Prescribing Information',
    url: 'https://www.ucb-usa.com/fintepla-prescribing-information.pdf',
    side: 'right',
  },
]

